/**
 * Conferência no portal da plataforma (E172.F7.T03, GUS-230 B1.3, GUS-216, ADR-0124 §1), na mesa
 * eletrônica. A tela mostra o que conferir, as conferências registradas e o registro de uma nova.
 * A pessoa consulta e anota; nada aqui abre o endereço por ela ou presume o resultado.
 *
 * E172.F19 (GUS-230 item 5): a conferência registrada renova as prévias; a página é avisada
 * (`onRegistrada`) e relê o card do pacote, que passa a mostrar a prévia em atualização.
 *
 * E180.F2 (GUS-254 item 3): com a consulta automática vigente (consistente ou divergente), o
 * resultado aparece como feito e o formulário fica atrás de "Corrigir".
 */
import type { AxiosInstance } from 'axios';
import { useState, type ReactNode } from 'react';

import { Blueprint, Button } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type { ConferenciaRegistradaData } from '@/lib/conferenciaNoPortal';
import { formatarDataHora } from '@/lib/trilha_format';
import { useConferenciaNoPortal, type EnvioDaConferencia } from '@/lib/useConferenciaNoPortal';

import { AlvoDoPortal } from './AlvoDoPortal';
import { ConferenciasRegistradas } from './ConferenciasRegistradas';
import { FormularioDaConferencia } from './FormularioDaConferencia';

const t = ptBR.conferenciaNoPortal;

function AvisoDoEnvio({ envio }: { envio: EnvioDaConferencia }): JSX.Element | null {
  if (envio.estado === 'registrada') {
    return (
      <p role="status" className="text-body-sm text-success-700" data-testid="portal-registrada-ok">
        {t.formulario.registrada}
      </p>
    );
  }
  if (envio.estado === 'recusada') {
    return (
      <p role="alert" className="text-body-sm text-danger-700" data-testid="portal-recusada">
        {t.erro[envio.motivo]}
      </p>
    );
  }
  return null;
}

/** E180.F2 (GUS-254 item 3): a vigente é a consulta do sistema que respondeu com um resultado. */
export function feitaPeloSistema(registradas: readonly ConferenciaRegistradaData[]): boolean {
  const vigente = registradas[0]?.conferencia;
  return (
    vigente !== undefined &&
    vigente.origem === 'sistema' &&
    (vigente.estado === 'consistente' || vigente.estado === 'divergente')
  );
}

/**
 * E203.F5.T04 (ADR-0146): a consulta automática vigente que conferiu, sem divergência. Na mesa ela
 * vira uma linha ("Conferido no portal em … · Corrigir"); a divergente continua à vista.
 */
export function conferidaSemDivergencia(
  registradas: readonly ConferenciaRegistradaData[],
): ConferenciaRegistradaData | null {
  const vigente = registradas[0];
  const c = vigente?.conferencia;
  return c !== undefined && c.origem === 'sistema' && c.estado === 'consistente'
    ? (vigente ?? null)
    : null;
}

function UmaLinha({
  registrada,
  children,
}: {
  registrada: ConferenciaRegistradaData;
  children: ReactNode;
}): JSX.Element {
  const [aberto, setAberto] = useState(false);
  const quando = registrada.conferencia.consultado_em ?? registrada.registrada_em;
  return (
    <section className="mt-sc-6 space-y-sc-3" data-testid="conferencia-no-portal-linha">
      <p className="flex flex-wrap items-center gap-sc-2 text-body-sm text-fg">
        <span data-testid="portal-conferido-em">
          {t.conferidoEm.replace('{data}', formatarDataHora(quando))}
        </span>
        <span aria-hidden="true">·</span>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-expanded={aberto}
          onClick={() => setAberto((a) => !a)}
          data-testid="portal-corrigir"
        >
          {aberto ? t.recolher : t.corrigir}
        </Button>
      </p>
      {aberto ? children : null}
    </section>
  );
}

/** O registro manual atrás de "Corrigir" quando o sistema já consultou; aberto quando não. */
function RegistroFacultativo({
  facultativo,
  children,
}: {
  facultativo: boolean;
  children: ReactNode;
}): JSX.Element {
  const [aberto, setAberto] = useState(false);
  if (!facultativo) return <>{children}</>;
  return (
    <div className="space-y-sc-2" data-testid="portal-registro-facultativo">
      <p className="text-body-sm text-fg-muted">{t.feitaPeloSistema}</p>
      <Button
        type="button"
        variant="secondary"
        size="sm"
        aria-expanded={aberto}
        onClick={() => setAberto((a) => !a)}
        data-testid="portal-corrigir"
      >
        {aberto ? t.recolher : t.corrigir}
      </Button>
      {aberto ? children : null}
    </div>
  );
}

export interface ConferenciaNoPortalProps {
  client: AxiosInstance;
  caseId: string;
  /** E172.F19: a conferência foi registrada; a página relê o que depende dela. */
  onRegistrada?: (() => void) | undefined;
  /** E203.F5.T04: na mesa, a automática consistente sai numa linha, com "Corrigir". */
  numaLinha?: boolean;
}

export function ConferenciaNoPortal({
  client,
  caseId,
  onRegistrada,
  numaLinha = false,
}: ConferenciaNoPortalProps): JSX.Element {
  const { painel, envio, reler, registrar } = useConferenciaNoPortal(client, caseId);
  const registrarEAvisar: typeof registrar = async (entrada, print) => {
    const registrada = await registrar(entrada, print);
    if (registrada) onRegistrada?.();
    return registrada;
  };
  const conferida =
    numaLinha && painel.estado === 'pronto'
      ? conferidaSemDivergencia(painel.dados.registradas)
      : null;
  if (conferida !== null && painel.estado === 'pronto') {
    return (
      <UmaLinha registrada={conferida}>
        <AvisoDoEnvio envio={envio} />
        <FormularioDaConferencia
          alvo={painel.dados.alvo}
          enviando={envio.estado === 'enviando'}
          onRegistrar={registrarEAvisar}
        />
      </UmaLinha>
    );
  }
  return (
    <Blueprint
      as="section"
      className="mt-sc-6 space-y-sc-4 p-sc-6"
      data-testid="conferencia-no-portal"
      data-estado={painel.estado}
      aria-labelledby="conferencia-no-portal-titulo"
    >
      <div>
        <h3 id="conferencia-no-portal-titulo" className="text-body-lg text-fg">
          {t.titulo}
        </h3>
      </div>
      {painel.estado === 'carregando' ? (
        <p className="text-body-sm text-fg-muted">{t.carregando}</p>
      ) : null}
      {painel.estado === 'erro' ? (
        <div role="alert" className="space-y-sc-2" data-testid="portal-erro">
          <p className="text-body-sm text-danger-700">{t.erro[painel.motivo]}</p>
          {painel.motivo === 'sem_permissao' || painel.motivo === 'nao_encontrado' ? null : (
            <Button type="button" variant="secondary" size="sm" onClick={reler}>
              {t.tentarDeNovo}
            </Button>
          )}
        </div>
      ) : null}
      {painel.estado === 'pronto' ? (
        <>
          <AlvoDoPortal alvo={painel.dados.alvo} />
          <ConferenciasRegistradas
            registradas={painel.dados.registradas}
            client={client}
            caseId={caseId}
          />
          <AvisoDoEnvio envio={envio} />
          <RegistroFacultativo facultativo={feitaPeloSistema(painel.dados.registradas)}>
            <FormularioDaConferencia
              alvo={painel.dados.alvo}
              enviando={envio.estado === 'enviando'}
              onRegistrar={registrarEAvisar}
            />
          </RegistroFacultativo>
        </>
      ) : null}
    </Blueprint>
  );
}
